/**
 * Inline Editing JavaScript Enhancements
 * Provides mobile-optimized inline editing functionality for quote line items
 */
import { isMobile, getLangPrefix, gettext } from './modules/utils.js';

// Exported init function called by main.js
export function init() {
    initializeInlineEditing();
    setupKeyboardNavigation();
    setupMobileOptimizations();
    setupAutoSave();
}

/**
 * Global function to edit a line item (called from templates)
 */
window.editLineItem = function(lineItemId) {
    const editableField = document.querySelector(`#line-item-${lineItemId} .editable-field`);
    if (editableField) {
        editableField.click();
    }
};

/**
 * Global function to cancel editing a line item (called from templates)
 */
window.cancelEditLineItem = function(lineItemId) {
    cancelCurrentEdit();
};

/**
 * Initialize inline editing functionality
 */
function initializeInlineEditing() {
    // Add visual indicators for editable fields
    document.querySelectorAll('.editable-field').forEach(field => {
        field.addEventListener('mouseenter', showEditIndicator);
        field.addEventListener('mouseleave', hideEditIndicator);
        field.addEventListener('focus', showEditIndicator);
        field.addEventListener('blur', hideEditIndicator);
        
        // Add touch support for mobile
        field.addEventListener('touchstart', handleTouchStart, { passive: true });
    });
    
    // Handle form submissions with loading states
    document.addEventListener('htmx:beforeRequest', handleBeforeRequest);
    document.addEventListener('htmx:afterRequest', handleAfterRequest);
    
    // Handle inline editing specific errors (before general error handler)
    document.addEventListener('htmx:responseError', handleResponseError);
}

/**
 * Setup keyboard navigation support
 */
function setupKeyboardNavigation() {
    document.addEventListener('keydown', function(e) {
        const activeElement = document.activeElement;
        
        // Tab navigation between editable fields
        if (e.key === 'Tab' && activeElement.classList.contains('inline-input')) {
            const editableFields = Array.from(document.querySelectorAll('.editable-field'));
            const currentIndex = editableFields.findIndex(field => 
                field.contains(activeElement)
            );
            
            if (currentIndex !== -1) {
                e.preventDefault();
                const nextIndex = e.shiftKey 
                    ? (currentIndex - 1 + editableFields.length) % editableFields.length
                    : (currentIndex + 1) % editableFields.length;
                
                // Cancel current edit and move to next field
                const nextField = editableFields[nextIndex];
                if (nextField) {
                    cancelCurrentEdit();
                    setTimeout(() => nextField.click(), 100);
                }
            }
        }
        
        // Enter to confirm edit
        if (e.key === 'Enter' && activeElement.classList.contains('inline-input')) {
            const form = activeElement.closest('.inline-edit-form');
            if (form) {
                form.dispatchEvent(new Event('submit'));
            }
        }
        
        // Escape to cancel edit
        if (e.key === 'Escape') {
            cancelCurrentEdit();
        }
    });
}

/**
 * Setup mobile-specific optimizations
 */
function setupMobileOptimizations() {
    if (isMobile()) {
        // Add mobile-specific classes
        document.body.classList.add('mobile-device');
        
        // iOS focus-zoom is avoided with a 16px inline-input font on small
        // screens (inline_editing.css) rather than user-scalable=no, which
        // took pinch-zoom away from users who need it.
        
        // Add haptic feedback for supported devices
        if ('vibrate' in navigator) {
            document.addEventListener('click', function(e) {
                if (e.target.classList.contains('editable-field') || 
                    e.target.closest('.editable-field')) {
                    navigator.vibrate(10); // Short vibration
                }
            });
        }
        
        // Setup swipe gestures for field navigation
        setupSwipeNavigation();
    }
}

/**
 * Setup swipe navigation for mobile devices
 */
function setupSwipeNavigation() {
    let touchStartX = 0;
    let touchStartY = 0;
    
    document.addEventListener('touchstart', function(e) {
        if (e.target.classList.contains('inline-input')) {
            touchStartX = e.touches[0].clientX;
            touchStartY = e.touches[0].clientY;
        }
    }, { passive: true });
    
    document.addEventListener('touchend', function(e) {
        if (e.target.classList.contains('inline-input')) {
            const touchEndX = e.changedTouches[0].clientX;
            const touchEndY = e.changedTouches[0].clientY;
            
            const deltaX = touchEndX - touchStartX;
            const deltaY = touchEndY - touchStartY;
            
            // Check if it's a horizontal swipe (and not vertical scroll)
            if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
                const editableFields = Array.from(document.querySelectorAll('.editable-field'));
                const currentField = e.target.closest('.editable-field');
                const currentIndex = editableFields.indexOf(currentField);
                
                if (currentIndex !== -1) {
                    const nextIndex = deltaX > 0 
                        ? (currentIndex + 1) % editableFields.length
                        : (currentIndex - 1 + editableFields.length) % editableFields.length;
                    
                    // Move to next/previous field
                    cancelCurrentEdit();
                    setTimeout(() => editableFields[nextIndex].click(), 100);
                }
            }
        }
    }, { passive: true });
}

/**
 * Setup auto-save functionality
 */
function setupAutoSave() {
    let autoSaveTimeout;
    
    document.addEventListener('input', function(e) {
        if (e.target.classList.contains('inline-input')) {
            // Clear existing timeout
            clearTimeout(autoSaveTimeout);
            
            // Set new timeout for auto-save
            autoSaveTimeout = setTimeout(() => {
                const form = e.target.closest('.inline-edit-form');
                if (form) {
                    // Add auto-save indicator
                    showAutoSaveIndicator(e.target);
                    
                    // Trigger form submission
                    form.dispatchEvent(new Event('submit'));
                }
            }, 2000); // Auto-save after 2 seconds of inactivity
        }
    });
}

/**
 * Handle touch start events for mobile
 */
function handleTouchStart(e) {
    // Add touch feedback
    this.classList.add('touched');
    setTimeout(() => this.classList.remove('touched'), 150);
}

/**
 * Show edit indicator on hover/focus
 */
function showEditIndicator(e) {
    const svg = this.querySelector('svg');
    if (svg) {
        svg.style.opacity = '1';
    }
}

/**
 * Hide edit indicator when not hovering/focused
 */
function hideEditIndicator(e) {
    // Don't hide if currently editing
    if (!this.querySelector('.inline-input')) {
        const svg = this.querySelector('svg');
        if (svg) {
            svg.style.opacity = '0';
        }
    }
}

/**
 * Handle HTMX before request events
 */
function handleBeforeRequest(e) {
    const target = e.detail.elt;
    
    if (target.classList.contains('inline-edit-form')) {
        // Add loading state
        target.classList.add('inline-loading');
        
        // Disable form elements
        const inputs = target.querySelectorAll('input, select, textarea');
        inputs.forEach(input => input.disabled = true);
    }
}

/**
 * Handle HTMX after request events
 */
function handleAfterRequest(e) {
    const target = e.detail.elt;
    
    if (target && target.classList && target.classList.contains('inline-edit-form')) {
        // Remove loading state
        target.classList.remove('inline-loading');
        
        // Re-enable form elements
        const inputs = target.querySelectorAll('input, select, textarea');
        inputs.forEach(input => input.disabled = false);
        
        // Show success feedback if successful
        if (e.detail.xhr.status === 200) {
            showSuccessFeedback(target);
        }
    }
}

/**
 * Handle HTMX response errors
 */
function handleResponseError(e) {
    const target = e.detail.elt;
    
    if (target.classList.contains('inline-edit-form')) {
        // Remove loading state
        target.classList.remove('inline-loading');
        
        // Re-enable form elements
        const inputs = target.querySelectorAll('input, select, textarea');
        inputs.forEach(input => input.disabled = false);

        const status = e.detail && e.detail.xhr ? e.detail.xhr.status : null;

        if (status === 403) {
            // Permission errors handled globally; don't show inline error copy
            return;
        }
        
        // Show error feedback
        showErrorFeedback(target, gettext('Failed to save changes. Please try again.'));
        
        // Mark as handled to prevent duplicate notifications
        e.detail.handled = true;
    }
}

/**
 * Cancel current edit and return to display mode
 */
function cancelCurrentEdit() {
    const activeInput = document.querySelector('.inline-input:focus');
    if (activeInput) {
        const form = activeInput.closest('.inline-edit-form');
        if (form) {
            // Trigger cancel by reloading display mode
            const fieldName = activeInput.name;
            const lineItemMatch = form.closest('[id^="line-item-"]');
            if (lineItemMatch) {
                const lineItemId = lineItemMatch.id.replace('line-item-', '');
                // Invoices and quotes each have their own display endpoint.
                const quoteId = form.closest('[data-quote-id]')?.dataset.quoteId;
                const invoiceId = form.closest('[data-invoice-id]')?.dataset.invoiceId;
                const parentPath = quoteId ? `quotes/${quoteId}` : (invoiceId ? `invoices/${invoiceId}` : null);
                
                if (parentPath && lineItemId && fieldName) {
                    const prefix = getLangPrefix();
                    htmx.ajax('GET', `${prefix}/${parentPath}/line-items/${lineItemId}/field/${fieldName}/display/`, {
                        target: form,
                        swap: 'outerHTML'
                    });
                }
            }
        }
    }
}

/**
 * Show auto-save indicator
 */
function showAutoSaveIndicator(element) {
    // Create or update auto-save indicator
    let indicator = element.parentNode.querySelector('.auto-save-indicator');
    if (!indicator) {
        indicator = document.createElement('div');
        indicator.className = 'auto-save-indicator text-xs text-gray-500 mt-1';
        element.parentNode.appendChild(indicator);
    }
    
    indicator.textContent = gettext('Auto-saving...');
    indicator.style.opacity = '1';
    
    // Hide after successful save
    setTimeout(() => {
        if (indicator) {
            indicator.style.opacity = '0';
            setTimeout(() => {
                if (indicator && indicator.parentNode) {
                    indicator.parentNode.removeChild(indicator);
                }
            }, 300);
        }
    }, 1500);
}

/**
 * Show success feedback
 */
function showSuccessFeedback(element) {
    element.classList.add('inline-success');
    setTimeout(() => element.classList.remove('inline-success'), 2000);
}

/**
 * Show error feedback
 */
function showErrorFeedback(element, message) {
    // Create error message element
    const errorDiv = document.createElement('div');
    errorDiv.className = 'inline-error';
    errorDiv.textContent = message;
    
    // Insert after the input
    const input = element.querySelector('.inline-input');
    if (input) {
        input.parentNode.insertBefore(errorDiv, input.nextSibling);
        
        // Remove error message after 3 seconds
        setTimeout(() => {
            if (errorDiv && errorDiv.parentNode) {
                errorDiv.parentNode.removeChild(errorDiv);
            }
        }, 3000);
    }
}

/**
 * Utility function to get quote ID from context
 */
function getQuoteIdFromContext(element) {
    // Try to find quote ID from various sources
    const quoteElement = element.closest('[data-quote-id]');
    if (quoteElement) {
        return quoteElement.dataset.quoteId;
    }
    
    // Fallback: extract from URL patterns
    const urlMatch = window.location.pathname.match(/\/quotes\/(\d+)\//);
    return urlMatch ? urlMatch[1] : null;
}

// Export functions for potential external use
window.InlineEditing = {
    cancelCurrentEdit,
    showSuccessFeedback,
    showErrorFeedback
};
